import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import DatePicker from '../../dateTime';
import dayjs from 'dayjs';
import { isEmpty } from '../../../helpers/common';
import { getCustomerList } from '../../../services/customerServices';
import { Form, Formation, FormBlock, FormButton, FormView, InputDiv, Label, ModalBlock, SelectedText } from './styles';
import Button from '../../button';
import SVGIcon from '../../../assets/images/svg/SVGIcon';
import UserPreferenceSingleton from '../../../helpers/userPreferenceSingleton';
import {
  createNewProject,
  dispatchProjectError,
  dispatchProjectSuccess,
  updateProjectData
} from '../../../services/projectServices';
import { trackAnalyticActivity } from '../../../services/analyticsService';
import { captureException } from '../../../services/logService';
import { CreateProjectPayloadInterface } from '../../../interfaces/ProjectInterface';
import { PROJECT_ANALYTICS } from '../../../global/analyticsConstants';
import { Dropdown, DropdownItem } from '../../dropdown';
import { useMobileDevice } from '../../../global/useMobile';
import { rootStore } from '../../../mobx/rootStore';
import { CustomerDetailsInterface } from '../../../interfaces/CustomerInterface';
import { observer } from 'mobx-react-lite';
import { syncProjects } from '../../../services/syncService';
import { BUTTON_TYPE, ICON_SIZE } from '../../../global/constants';
import Input from '../../input';
import { ROUTES } from '../../../routes/urls';

interface Props {
  handleCloseModal?: () => void;
  loadProjectData?: () => void;
  onClickCancel?: () => void;
}

const CreateNewProjectModal: React.FC<Props> = ({ handleCloseModal, loadProjectData, onClickCancel }) => {
  //States
  const [loading, setLoading] = useState(false);
  const [isCustomerDropdownOpen, setIsCustomerDropdownOpen] = useState(false);
  const [customerDetails, setCustomerDetails] = useState<CustomerDetailsInterface | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearch, setIsSearch] = useState(false);
  const isMobile = useMobileDevice();
  const isMinWidth = isMobile ? Math.max(window.innerWidth - 65) : 592;

  //Mobx store variables
  const { projectStore, workspaceStore, customerStore } = rootStore;
  const { createProjectInput } = projectStore;
  const { name, dueDate, customerId, startDate, templateId } = createProjectInput;
  const { currentWorkspace: workspace } = workspaceStore;
  const { customers } = customerStore;
  const workspace_id = workspace?.id;
  //Other variable
  const history = useHistory();

  const updateCustomer = useCallback((customer: CustomerDetailsInterface) => {
    setCustomerDetails(customer);
    rootStore.projectStore.createProjectInput.setCustomerId(customer?.id);
  }, []);

  const loadData = useCallback(async () => {
    if (!isEmpty(workspace_id)) {
      if (!startDate) rootStore.projectStore.createProjectInput.setStartDate(new Date());
      const customerResponse = await getCustomerList();
      const customer = customerResponse?.find((x: { id: string }) => x.id === createProjectInput?.customerId);
      updateCustomer(customer);
    }
  }, [workspace_id, startDate, updateCustomer, createProjectInput?.customerId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const onChangeSearch = useCallback((value: string, isTemplate?: boolean) => {
    setIsSearch(true);
    setSearchQuery(value);
  }, []);

  const onSubmit = useCallback(
    async (e: React.SyntheticEvent) => {
      e.preventDefault();
      if (!startDate) {
        dispatchProjectError('Please select start date');
        return;
      }
      if (dueDate && startDate && startDate >= dueDate) {
        dispatchProjectError('Start date must be before due date');
        return;
      }
      try {
        setLoading(true);
        let result;
        const userDetails = UserPreferenceSingleton.getInstance().getCurrentUser();
        if (!isEmpty(createProjectInput?.id)) {
          if (!workspace?.isAdmin) return dispatchProjectError(`You can't update project.`);
          if (isEmpty(name)) return dispatchProjectError('Please enter project name');
          const updatePayload = {
            Name: name,
            CustomerId: !isEmpty(customerId) ? customerId : null,
            StartDate: startDate ? startDate?.toISOString() : undefined,
            DueDate: dueDate ? dueDate?.toISOString() : undefined,
            removeStartDate: !startDate ? true : undefined,
            removeDueDate: !dueDate ? true : undefined
          };
          result = await updateProjectData(createProjectInput?.id, updatePayload);
          if (result && createProjectInput?.id) {
            trackAnalyticActivity(PROJECT_ANALYTICS.UPDATED);
            await syncProjects();
            rootStore.projectStore.setCurrentProject(createProjectInput?.id);
            dispatchProjectSuccess('Project details updated successfully!');
          }
        } else {
          if (!workspace?.isAdmin) return dispatchProjectError(`You can't create project.`);
          if (isEmpty(name)) return dispatchProjectError('Please enter project name');
          const createPayload: CreateProjectPayloadInterface = {
            name: name || '',
            companyId: workspace_id || '',
            isEstimate: false,
            userId: userDetails?.id,
            description: ''
          };
          if (customerId) {
            createPayload.customerId = customerId;
          }
          if (templateId) {
            createPayload.templateId = templateId;
          }
          if (startDate) {
            createPayload.startDate = startDate?.toISOString();
          }
          if (dueDate) {
            createPayload.dueDate = dueDate?.toISOString();
          }
          result = await createNewProject(createPayload);
          if (result) {
            await syncProjects();
            rootStore.projectStore.setCurrentProject(result?.data['_id']);
            trackAnalyticActivity(PROJECT_ANALYTICS.CREATED, { from_template: !isEmpty(createPayload?.templateId) });
            history.push(ROUTES.PROJECT_TASKS.replace(':id', result?.data['_id'] || ''));
            dispatchProjectSuccess('Project Added successfully!');
          }
        }
        if (result) {
          if (handleCloseModal) handleCloseModal();
          if (loadProjectData) {
            loadProjectData();
          }
          rootStore.projectStore.createProjectInput.clear();
        }
      } catch (e) {
        captureException(e);
      } finally {
        setLoading(false);
      }
    },
    [
      startDate,
      dueDate,
      createProjectInput?.id,
      workspace?.isAdmin,
      name,
      customerId,
      workspace_id,
      templateId,
      history,
      handleCloseModal,
      loadProjectData
    ]
  );
  const handleToggleCustomerDropdown = useCallback(() => {
    setIsCustomerDropdownOpen((prev) => !prev);
  }, []);

  const renderCustomerDropdown = useMemo(() => {
    const filteredCustomer = !isSearch
      ? customers
      : customers?.filter((x) => x?.value?.trim().toLowerCase().includes(searchQuery.toLowerCase()));
    return filteredCustomer?.map((item) => {
      const isSelected = item.id === customerId;
      return (
        <DropdownItem
          key={item.id}
          label={item.value}
          onClick={() => {
            setIsSearch(false);
            setSearchQuery('');
            updateCustomer(item);
            setIsCustomerDropdownOpen(false);
          }}
          isSuffix={
            isSelected && (
              <SVGIcon
                name='checkbox-icon'
                width={ICON_SIZE.MD}
                height={ICON_SIZE.MD}
                viewBox={`0 0 ${ICON_SIZE.MD} ${ICON_SIZE.MD}`}
                fill='none'
              />
            )
          }
        />
      );
    });
  }, [customerId, customers, isSearch, searchQuery, updateCustomer]);

  return (
    <>
      <ModalBlock onSubmit={onSubmit}>
        <Formation>
          <Input
            label={
              <Label>
                Project Name <span>*</span>
              </Label>
            }
            placeHolder='Enter project name'
            value={name}
            onChange={(e) => rootStore.projectStore.createProjectInput.setName(e.target.value)}
          />
          {customers?.length > 0 && (
            <FormView>
              <Form>
                <Label>Customer</Label>
                <Dropdown
                  modalTitle='Customer'
                  isMinWidth={isMinWidth}
                  onOutsideClick={() => setIsCustomerDropdownOpen(false)}
                  content={
                    <InputDiv role='button' onClick={handleToggleCustomerDropdown}>
                      <Input
                        placeHolder={isEmpty(customerDetails?.companyName) ? 'Select a customer' : ''}
                        value={searchQuery}
                        onChange={(e) => onChangeSearch(e.target.value)}
                        trailingIconName='down-arrow-icon'
                        trailingIconClassName={`angleDown-icon ${isCustomerDropdownOpen ? 'rotate' : ''}`}
                      />
                      {isEmpty(searchQuery) && <SelectedText>{customerDetails?.companyName}</SelectedText>}
                    </InputDiv>
                  }
                  trigger='click'>
                  {renderCustomerDropdown}
                </Dropdown>
              </Form>
            </FormView>
          )}
          <FormBlock>
            <Form>
              <Label>Start Date</Label>
              <DatePicker
                label='Select start date'
                value={startDate}
                inputFormat='DD/MM/YYYY'
                onChange={(date: dayjs.Dayjs | null) => {
                  if (date === null) {
                    rootStore.projectStore.createProjectInput.setStartDate(null);
                  } else {
                    rootStore.projectStore.createProjectInput.setStartDate(date.toDate());
                  }
                }}
              />
            </Form>
            <Form>
              <Label>Estimated Due Date</Label>
              <DatePicker
                label='Select due date'
                value={dueDate || null}
                inputFormat='DD/MM/YYYY'
                onChange={(date: dayjs.Dayjs | null) => {
                  if (date === null) {
                    rootStore.projectStore.createProjectInput.setDueDate(null);
                  } else {
                    rootStore.projectStore.createProjectInput.setDueDate(date.toDate());
                  }
                }}
              />
            </Form>
          </FormBlock>
        </Formation>
        <FormButton>
          <Button title='Cancel' variant={BUTTON_TYPE.SECONDARY} onClick={onClickCancel} buttonType='button' />
          <Button
            buttonType={'submit'}
            variant={BUTTON_TYPE.PRIMARY}
            title={isEmpty(createProjectInput?.id) ? 'Create Project' : 'Update Project'}
            isLoading={loading}
          />
        </FormButton>
      </ModalBlock>
    </>
  );
};

export default observer(CreateNewProjectModal);
